import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Container, Typography, Box, Grid, Card, CardContent } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import GrassIcon from '@mui/icons-material/Grass';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import PaletteIcon from '@mui/icons-material/Palette';

// Animációk
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Styled komponensek
const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: `
    linear-gradient(135deg, 
      rgba(76, 175, 80, 0.1) 0%, 
      rgba(139, 195, 74, 0.1) 25%,
      rgba(76, 175, 80, 0.05) 50%,
      rgba(139, 195, 74, 0.1) 75%,
      rgba(76, 175, 80, 0.1) 100%
    ),
    radial-gradient(circle at 20% 80%, rgba(76, 175, 80, 0.2) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(139, 195, 74, 0.2) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(255, 152, 0, 0.1) 0%, transparent 50%)
  `,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234caf50' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
    `,
    opacity: 0.5,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '200px',
    background: `
      url("data:image/svg+xml,%3Csvg viewBox='0 0 1200 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0,60 C150,100 350,0 600,60 C850,120 1050,20 1200,60 L1200,120 L0,120 Z' fill='%234caf50' fill-opacity='0.1'/%3E%3C/svg%3E")
    `,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  }
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  position: 'relative',
  zIndex: 2,
  animation: `${float} 6s ease-in-out infinite`,
  '&:hover': {
    animation: `${pulse} 2s ease-in-out infinite`,
  },
  '& img': {
    filter: 'drop-shadow(0 8px 32px rgba(76, 175, 80, 0.3))',
    transition: 'all 0.3s ease',
  },
  '&:hover img': {
    transform: 'scale(1.05)',
    filter: 'drop-shadow(0 12px 40px rgba(76, 175, 80, 0.4))',
  }
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Playfair Display", "Georgia", "Times New Roman", serif',
  fontWeight: 700,
  fontSize: 'clamp(2.5rem, 8vw, 4.5rem)', // Responsive font size
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(2),
  position: 'relative',
  zIndex: 1,
  animation: `${fadeInUp} 1s ease-out`,
  textAlign: 'center',
  letterSpacing: '0.02em',
  lineHeight: 1.1,
  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
  // Fallback színek ha a gradient nem működik
  color: theme.palette.primary.main,
  // Mobilon jobb megjelenítés
  [theme.breakpoints.down('sm')]: {
    fontSize: 'clamp(2rem, 10vw, 3rem)',
    marginBottom: theme.spacing(1.5),
    letterSpacing: '0.01em',
  },
  // Tablet nézetben
  [theme.breakpoints.between('sm', 'md')]: {
    fontSize: 'clamp(3rem, 8vw, 3.5rem)',
  }
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(4),
  maxWidth: '600px',
  margin: '0 auto',
  lineHeight: 1.6,
  position: 'relative',
  zIndex: 1,
  animation: `${fadeInUp} 1s ease-out 0.2s both`,
}));

const CTAButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: 'white',
  padding: theme.spacing(1.5, 4),
  fontSize: '1.1rem',
  fontWeight: 600,
  borderRadius: '50px',
  textTransform: 'none',
  boxShadow: `0 8px 25px ${theme.palette.primary.main}40`,
  position: 'relative',
  zIndex: 1,
  animation: `${fadeInUp} 1s ease-out 0.4s both`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 35px ${theme.palette.primary.main}50`,
    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
  }
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: '20px',
  border: `1px solid ${theme.palette.primary.main}20`,
  background: `linear-gradient(135deg, 
    ${theme.palette.background.paper} 0%, 
    ${theme.palette.primary.main}05 100%)`,
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeInUp} 1s ease-out 0.6s both`,
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 20px 40px ${theme.palette.primary.main}20`,
    '& .feature-icon': {
      animation: `${pulse} 1s ease-in-out`,
      color: theme.palette.primary.main,
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  }
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.main}25)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  '& .MuiSvgIcon-root': {
    fontSize: '2rem',
    color: theme.palette.primary.main,
    transition: 'all 0.3s ease',
  }
}));

const FeatureTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const FeatureDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.6,
}));

const SectionContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(8),
  marginBottom: theme.spacing(8),
}));

const Home: React.FC = () => {
  const { user } = useAuth();

  const features = [
    {
      icon: <PaletteIcon className="feature-icon" />,
      title: 'Intuitív Tervezés',
      description: 'Drag & drop felülettel könnyedén helyezd el a növényeket és kerti elemeket. Valós idejű előnézet segít a döntésekben.'
    },
    {
      icon: <LocalFloristIcon className="feature-icon" />,
      title: 'Gazdag Növényadatbázis',
      description: 'Több ezer növényfaj részletes adataival, beleértve a gondozási útmutatókat és kompatibilitási információkat.'
    },
    {
      icon: <ViewInArIcon className="feature-icon" />,
      title: '3D Megjelenítés',
      description: 'Tekintsd meg kerted háromdimenziós nézetben, hogy valós képet kapj a végeredményről minden évszakban.'
    }
  ];

  return (
    <Box>
      <HeroSection>
        <Container maxWidth="lg">
          <LogoContainer>
            <img 
              src="/logo_tb.png" 
              alt="KertMagad logó" 
              style={{ 
                height: '220px', 
                width: 'auto',
                maxWidth: '100%'
              }}
            />
          </LogoContainer>
          
          <MainTitle variant="h1">
            KertMagad
          </MainTitle>
          
          <Subtitle variant="h5">
            Tervezd meg álmaid kertjét intelligens eszközökkel és AI támogatással. 
            Hozd létre a tökéletes zöld oázist, amely tükrözi személyiségedet.
          </Subtitle>
          
          <Box mt={4}>
            {user ? (
              <Link to="/gardens" style={{ textDecoration: 'none' }}>
                <CTAButton
                  size="large"
                  startIcon={<GrassIcon />}
                >
                  Saját kertek megtekintése
                </CTAButton>
              </Link>
            ) : (
              <Link to="/login" style={{ textDecoration: 'none' }}>
                <CTAButton
                  size="large"
                  startIcon={<LocalFloristIcon />}
                >
                  Kezdjük el a tervezést
                </CTAButton>
              </Link>
            )}
          </Box>
        </Container>
      </HeroSection>

      <SectionContainer maxWidth="lg">
        <Box textAlign="center" mb={6}>
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mb: 2
            }}
          >
            Miért válaszd a KertMagad-ot?
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ maxWidth: '600px', margin: '0 auto' }}
          >
            Professzionális kerttervezési eszközök mindenki számára elérhetően
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <FeatureCard>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <FeatureIcon>
                    {feature.icon}
                  </FeatureIcon>
                  <FeatureTitle variant="h6">
                    {feature.title}
                  </FeatureTitle>
                  <FeatureDescription variant="body2">
                    {feature.description}
                  </FeatureDescription>
                </CardContent>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </SectionContainer>
    </Box>
  );
};

export default Home; 